import {Company, DateTime, EmailAddress} from "@/modules/models";
import { nonemptyArray, noNullApply, requireNum, requireStr} from "@/modules/utils";

export enum WhenType {
    Daily = "daily",
    Weekly = "weekly",
}

const whenTypeTextMap = new Map<WhenType, string>();
whenTypeTextMap.set(WhenType.Daily, "Daglig");
whenTypeTextMap.set(WhenType.Weekly, "Ukentlig");

export const whenTypeToText = (wt: WhenType) => whenTypeTextMap.get(wt);

export enum WeekDays {
    Mon = "monday",
    Tue = "tuesday",
    Wed = "wednesday",
    Thu = "thursday",
    Fri = "friday",
    Sat = "saturday",
    Sun = "sunday",
}

const weekDaysMap = new Map<WeekDays, string>();
weekDaysMap.set(WeekDays.Mon, "Mandag");
weekDaysMap.set(WeekDays.Tue, "Tirsdag");
weekDaysMap.set(WeekDays.Wed, "Onsdag");
weekDaysMap.set(WeekDays.Thu, "Torsdag");
weekDaysMap.set(WeekDays.Fri, "Fredag");
weekDaysMap.set(WeekDays.Sat, "Lørdag");
weekDaysMap.set(WeekDays.Sun, "Søndag");

export const weekDayToText = (wd: WeekDays) => weekDaysMap.get(wd);

export const weekDayFromJson = (json: any) => {
    if(!json) {
        return null;
    }
    const str = requireStr(json);
    switch (str) {
        case WeekDays.Mon.toString(): return WeekDays.Mon;
        case WeekDays.Tue.toString(): return WeekDays.Tue;
        case WeekDays.Wed.toString(): return WeekDays.Wed;
        case WeekDays.Thu.toString(): return WeekDays.Thu;
        case WeekDays.Fri.toString(): return WeekDays.Fri;
        case WeekDays.Sat.toString(): return WeekDays.Sat;
        case WeekDays.Sun.toString(): return WeekDays.Sun;
    }
    throw new Error(`String "${json}" not found in WeekDays enum`);
}

export const whenTypeFromJson = (json: any) => {
    if(!json) {
        return null;
    }
    const str = requireStr(json);
    switch (str) {
        case WhenType.Daily.toString(): return WhenType.Daily;
        case WhenType.Weekly.toString(): return WhenType.Weekly;
    }
    throw new Error(`String "${json}" not found in WhenType enum`);
}

// Also defined in NotificationSensor.scala
export enum DatasourceNames {
    CardExpirationCompanyCards = "CardExpirationCompanyCards",
    CardExpirationCompanyLocations = "CardExpirationCompanyLocations",
}

export const datasourceSelectItems = [
    {
        text: "Snart utløpte kort tilhørende firma",
        value: DatasourceNames.CardExpirationCompanyCards,
    },
    {
        text: "Utløpte kort på anlegg tilhørende firma",
        value: DatasourceNames.CardExpirationCompanyLocations,
    },
];

export const datasourceNameToText = (ds: DatasourceNames): string => {
    const selectItem = datasourceSelectItems.find(it => it.value === ds)
    if(selectItem != null) {
        return selectItem.text;
    }
    throw new Error(`${ds} not mapped to text`);
}

const datasourceNameFromJson = (json: any) => {
    switch (json) {
        case DatasourceNames.CardExpirationCompanyCards.toString(): return DatasourceNames.CardExpirationCompanyCards;
        case DatasourceNames.CardExpirationCompanyLocations.toString(): return DatasourceNames.CardExpirationCompanyLocations;
    }
    throw new Error(`String "${json}" not found in DatasourceNames enum`);
}

export class NotificationSensor {
    constructor(
        public id: number,
        public datasourceName: DatasourceNames, // card-expiration-report-company-cards, card-expiration-report-company-locations, etc..
        public company: Company,
    ) {}

    static fromJson(json: any) {
        return new NotificationSensor(
            requireNum(json.id),
            datasourceNameFromJson(json.datasourceName),
            Company.fromJson(json.company),
        );
    }
}

export class NotificationSensorAndConfigId {
    public configId: number;
    public sensor: NotificationSensor;

    constructor(configId: number, sensor: NotificationSensor) {
        this.configId = configId;
        this.sensor = sensor;
    }
}

export class NotificationConfig {
    constructor(
        public id: number,
        public personRef: string,
        public toEmail: EmailAddress,
        public whenType: WhenType,
        public whenWeekday: WeekDays | null,
        public whenHour: number | null,
        public sensors: NotificationSensor[],
        public lastRun: DateTime | null,
    ) {}

    get whenTypeText() {
        return whenTypeToText(this.whenType);
    }
    get whenWeekDayText() {
        return this.whenWeekday && weekDayToText(this.whenWeekday) || null;
    }

    static fromJson(json: any) {
        const email = json.toEmail ? EmailAddress.fromJson(json.toEmail): null;
        if(!email) {
            throw new Error("to property containing email address is required");
        }
        const whenType = whenTypeFromJson(json.whenType);
        if(!whenType) {
            throw new Error(`whenType is required`);
        }
        return new NotificationConfig(
            requireNum(json.id),
            requireStr(json.personRef),
            email,
            whenType,
            weekDayFromJson(json.whenWeekday),
            requireNum(json.whenHour),
            nonemptyArray(json.sensors) ? json.sensors.map(r => NotificationSensor.fromJson(r)) : [],
            noNullApply(json.lastRun, DateTime.fromJson),
        )
    }

    clone(): NotificationConfig {
        return new NotificationConfig(this.id, this.personRef, this.toEmail, this.whenType, this.whenWeekday, this.whenHour, this.sensors, this.lastRun);
    }
}